<template>
  <button
    type="button"
    class="group relative flex flex-col overflow-hidden rounded-lg border border-obsidian-100 bg-white text-left"
    @click="emit('edit', video)"
  >
    <div class="relative aspect-video w-full overflow-hidden bg-obsidian-700">
      <img
        v-if="video.media.encodingStatus === 'READY' && video.media.posterFileUrl"
        :src="video.media.posterFileUrl"
        class="h-full w-full object-cover"
        alt=""
      />
      <div v-else class="flex h-full w-full items-center justify-center text-white">
        <VideoIcon class="size-10" />
      </div>

      <span
        class="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium backdrop-blur"
        :class="
          video.globalEntry ? 'bg-white/85 text-obsidian-800' : 'bg-obsidian-900/80 text-white'
        "
      >
        <component :is="video.globalEntry ? GlobeIcon : MapPinIcon" class="size-3" />
        {{ scopeLabel }}
      </span>

      <span
        v-if="isPaused"
        class="bg-obsidian-900/80 absolute right-2 top-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium text-white"
      >
        <PauseIcon class="size-3" /> {{ t('paused') }}
      </span>
    </div>

    <div class="flex flex-1 flex-col gap-3 p-3">
      <div class="flex items-start justify-between gap-2">
        <p class="line-clamp-2 font-medium">{{ video.media.title }}</p>
        <MediaEncodingStatusBadge :status="video.media.encodingStatus" size="sm" />
      </div>

      <div class="mt-auto flex items-center justify-between text-sm">
        <div class="flex items-center gap-1.5 text-obsidian-600">
          <ScaleIcon class="size-4 text-obsidian-400" />
          <span v-if="video.globalEntry">
            <span class="font-semibold text-obsidian-900">{{ video.globalEntry.weight }}</span>
            {{ t('global') }}
          </span>
          <span v-else class="text-obsidian-400">{{
            t('overrideCount', { count: video.overrides.length })
          }}</span>
        </div>

        <span
          v-if="video.overrides.length"
          class="inline-flex items-center gap-1 rounded-full bg-hazel-100 px-2 py-0.5 text-xs font-medium text-hazel-700"
        >
          <MapPinIcon class="size-3" /> {{ t('overrideCount', { count: video.overrides.length }) }}
        </span>
      </div>
    </div>
  </button>
</template>

<script lang="ts" setup>
import type { RotationVideoConfig } from '@/entities/media-rotation/rotation-media.entity';
import MediaEncodingStatusBadge from '@/components/badges/MediaEncodingStatusBadge.vue';
import { GlobeIcon, MapPinIcon, PauseIcon, ScaleIcon, VideoIcon } from 'lucide-vue-next';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps<{
  video: RotationVideoConfig;
}>();

const emit = defineEmits<{
  (e: 'edit', video: RotationVideoConfig): void;
}>();

const { t } = useI18n();

const isPaused = computed(
  () => props.video.entries.length > 0 && props.video.entries.every((entry) => entry.weight <= 0),
);

const scopeLabel = computed(() =>
  props.video.globalEntry
    ? t('global')
    : t('overrideCount', { count: props.video.overrides.length }),
);
</script>

<i18n lang="json">
{
  "en": {
    "global": "global",
    "overrideCount": "{count} stations",
    "paused": "Paused"
  },
  "de": {
    "global": "global",
    "overrideCount": "{count} Stationen",
    "paused": "Pausiert"
  },
  "es": {
    "global": "global",
    "overrideCount": "{count} estaciones",
    "paused": "En pausa"
  }
}
</i18n>
